// src/CodePlusTab.js
// Code+ — AI building-code research with citations. Self-contained: no new dependencies,
// inline styles only, talks to /api/codeplus/* with the same bearer token the rest of the app uses.
//
// Props (same shape as IntegrationsTab):
//   user          Auth0 user object (only used for display)
//   getAuthToken  () => string   e.g. () => localStorage.getItem('auth0_token')
//
// Also exports checkCodePlusAccess(getAuthToken) -> Promise<boolean>, used by App.js to decide
// whether to show the tab at all (the backend answers 403 when Code+ is off for the user's domain).

import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = 'https://apispeccheck.zonetap.com/api/codeplus';

// ---- theme: tweak these to match the rest of the app ----
const t = {
  text: '#111827',
  muted: '#6b7280',
  line: '#e5e7eb',
  panel: '#ffffff',
  bg: '#f9fafb',
  gold: '#D4A017',
  goldInk: '#1a1400',
  ok: '#15803d',
  warn: '#b45309',
  bad: '#b91c1c',
};

const S = {
  page: { padding: '24px 28px', color: t.text, maxWidth: 1200, margin: '0 auto', fontSize: 14, lineHeight: 1.5 },
  h1: { margin: '0 0 4px', fontSize: 22, fontWeight: 700 },
  sub: { color: t.muted, margin: '0 0 20px', fontSize: 13 },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) 1fr', gap: 20, alignItems: 'start' },
  card: { background: t.panel, border: `1px solid ${t.line}`, borderRadius: 10, padding: 16, marginBottom: 16 },
  h2: { margin: '0 0 10px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, color: t.muted },
  set: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderTop: `1px solid ${t.line}` },
  setName: { fontWeight: 600, overflowWrap: 'anywhere' },
  small: { color: t.muted, fontSize: 12.5 },
  tag: { display: 'inline-block', fontSize: 11, padding: '1px 7px', borderRadius: 99, border: `1px solid ${t.line}`, color: t.muted, marginLeft: 6 },
  tagPrivate: { borderColor: t.gold, color: t.warn },
  label: { display: 'block', fontSize: 13, color: t.muted, margin: '10px 0 4px' },
  input: { width: '100%', boxSizing: 'border-box', border: `1px solid ${t.line}`, borderRadius: 8, padding: '9px 10px', font: 'inherit', background: '#fff', color: t.text },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  attest: { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: t.muted, margin: '12px 0' },
  btn: { background: t.gold, color: t.goldInk, border: 0, borderRadius: 8, padding: '10px 16px', font: 'inherit', fontWeight: 600, cursor: 'pointer' },
  // "Choose File" uses the same gold button look; the native input is hidden (browsers ignore inline styles on it)
  fileRow: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
  fileName: { color: t.muted, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  btnOff: { opacity: 0.5, cursor: 'default' },
  linkBtn: { background: 'none', border: 0, color: t.warn, cursor: 'pointer', padding: 0, font: 'inherit', fontSize: 13, marginTop: 4 },
  xBtn: { background: 'none', border: 0, color: t.muted, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' },
  err: { color: t.bad, marginTop: 10, fontSize: 13 },
  answer: { whiteSpace: 'pre-wrap', margin: 0 },
  cite: { background: t.gold, color: t.goldInk, border: 0, borderRadius: 5, fontSize: 12, fontWeight: 700, padding: '0 5px', margin: '0 1px', cursor: 'pointer' },
  source: { borderTop: `1px solid ${t.line}`, padding: '10px 0' },
  snip: { color: t.muted, fontSize: 13.5, whiteSpace: 'pre-wrap', marginTop: 4 },
  notice: { fontSize: 12.5, color: t.muted, marginTop: 14 },
  blocked: { padding: 40, textAlign: 'center', color: t.muted },
  filterRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, margin: '0 0 8px' },
  select: { width: '100%', boxSizing: 'border-box', border: `1px solid ${t.line}`, borderRadius: 8, padding: '7px 8px', font: 'inherit', fontSize: 13, background: '#fff', color: t.text },
  groupHead: { display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0 2px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, color: t.muted, fontWeight: 600 },
  groupLinks: { marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontWeight: 400 },
  setList: { maxHeight: 380, overflowY: 'auto' },
  micBtn: { background: '#fff', color: t.text, borderWidth: 1, borderStyle: 'solid', borderColor: t.line, borderRadius: 8, padding: '10px 14px', font: 'inherit', fontWeight: 600, cursor: 'pointer' },
  micOn: { borderColor: t.bad, color: t.bad },
};

// "section 107 point 2 point 5" -> "section 107.2.5" (speech engines spell out the dots in code numbers)
export function normalizeSpoken(text) {
  let out = String(text || '');
  let prev;
  do {
    prev = out;
    out = out.replace(/(\d)\s*(?:point|dot)\s*(\d)/gi, '$1.$2');
  } while (out !== prev);
  return out.replace(/\s+/g, ' ').trim();
}

export async function checkCodePlusAccess(getAuthToken) {
  try {
    const res = await fetch(`${API}/sets`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    // 401 = stale sign-in token, not "Code+ is off": keep the tab visible so the user sees the
    // "session expired" message instead of the tab silently disappearing. 403/503/other = hide the tab.
    return res.ok || res.status === 401;
  } catch (e) {
    return false;
  }
}

// Suggest form values from the file name. Only fills what the user hasn't typed; everything stays editable.
function guessFromFilename(fileName) {
  const base = String(fileName || '').replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ').replace(/\s*-\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const name = (/[A-Z]/.test(base) ? base : base.replace(/\b[a-z]/g, (c) => c.toUpperCase())).slice(0, 120);
  const years = base.match(/\b(?:19|20)\d{2}\b/g); // part numbers like 1910 can look like years; the edition is usually the last one
  const year = years ? [years[years.length - 1]] : null;
  const publisher = /\bICC\b/i.test(base) ? 'ICC' : /\bIAPMO\b/i.test(base) ? 'IAPMO' : /\bNFPA\b/i.test(base) ? 'NFPA' : '';
  return { name, edition: year ? year[0] : '', jurisdiction: /\bcalifornia\b|\bCA\b/i.test(base) ? 'CA' : '', publisher };
}

export default function CodePlusTab({ user, getAuthToken }) {
  const [sets, setSets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [blocked, setBlocked] = useState(null); // string when the tab can't be used at all
  const [unchecked, setUnchecked] = useState({}); // set ids the user turned OFF (ready sets default ON)
  const [attestText, setAttestText] = useState('');
  const [form, setForm] = useState({ name: '', edition: '', jurisdiction: '', publisher: '', attest: false, file: null });
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [askErr, setAskErr] = useState('');
  const [result, setResult] = useState(null);
  const [full, setFull] = useState({}); // sectionId -> full text
  const [usage, setUsage] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const EMPTY_PUB = { file: null, name: '', edition: '', jurisdiction: '', publisher: '', sourceUrl: '', license: '' };
  const [pub, setPub] = useState(EMPTY_PUB);
  const [pubBusy, setPubBusy] = useState(false);
  const [pubErr, setPubErr] = useState('');
  const pubFileInput = useRef(null);
  const [listening, setListening] = useState(false);
  const [micErr, setMicErr] = useState('');
  const recRef = useRef(null);
  const [setQuery, setSetQuery] = useState('');
  const [setJur, setSetJur] = useState('');
  const [setSort, setSetSort] = useState('name');

  const mounted = useRef(true);
  const pollTimer = useRef(null);
  const fileInput = useRef(null);

  const api = useCallback(
    async (path, opts = {}) => {
      const token = getAuthToken ? getAuthToken() : null;
      const res = await fetch(`${API}${path}`, {
        ...opts,
        headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
      });
      let body = {};
      try {
        body = await res.json();
      } catch (e) {
        /* non-JSON error page (e.g. 401 from the auth layer) */
      }
      if (!res.ok) {
        const msg =
          res.status === 401
            ? 'Your session has expired. Please sign out (top right) and sign back in, then reopen this tab.'
            : body.error || `Request failed (${res.status})`;
        const err = new Error(msg);
        err.status = res.status;
        throw err;
      }
      return body;
    },
    [getAuthToken]
  );

  const loadSets = useCallback(async () => {
    try {
      const j = await api('/sets');
      if (!mounted.current) return;
      setSets(j.sets || []);
      setIsAdmin(j.admin === true);
      setBlocked(null);
      clearTimeout(pollTimer.current);
      if ((j.sets || []).some((s) => s.status === 'processing')) {
        pollTimer.current = setTimeout(loadSets, 2500);
      }
    } catch (e) {
      if (!mounted.current) return;
      setBlocked(
        e.status === 403
          ? 'Code+ is not enabled for your organization. Contact your administrator or hello@aecworks.ai.'
          : e.status === 503
          ? 'Code+ is starting up. Please try again in a moment.'
          : e.message
      );
    } finally {
      if (mounted.current) setLoaded(true);
    }
  }, [api]);

  useEffect(() => {
    mounted.current = true;
    loadSets();
    api('/attestation')
      .then((j) => mounted.current && setAttestText(j.text || ''))
      .catch(() => {});
    return () => {
      mounted.current = false;
      clearTimeout(pollTimer.current);
      try { if (recRef.current) recRef.current.abort(); } catch (e) { /* ignore */ }
    };
  }, [loadSets, api]);

  const selectedIds = sets.filter((s) => s.status === 'ready' && !unchecked[s.id]).map((s) => s.id);

  async function onUpload(e) {
    e.preventDefault();
    setUploadErr('');
    if (!form.file) return setUploadErr('Choose a PDF, DOCX or TXT file.');
    if (!form.name.trim()) return setUploadErr('Give the code set a name.');
    if (!form.attest) return setUploadErr('Please confirm the license statement.');
    const fd = new FormData();
    fd.append('file', form.file);
    fd.append('name', form.name.trim());
    fd.append('edition', form.edition);
    fd.append('jurisdiction', form.jurisdiction);
    fd.append('publisher', form.publisher);
    fd.append('attest', 'true');
    setUploading(true);
    try {
      await api('/sets', { method: 'POST', body: fd });
      setForm({ name: '', edition: '', jurisdiction: '', publisher: '', attest: false, file: null });
      if (fileInput.current) fileInput.current.value = '';
      await loadSets();
    } catch (ex) {
      setUploadErr(ex.message);
    } finally {
      if (mounted.current) setUploading(false);
    }
  }

  async function onPublish(e) {
    e.preventDefault();
    setPubErr('');
    if (!pub.file) return setPubErr('Choose a PDF, DOCX or TXT file.');
    if (!pub.name.trim()) return setPubErr('Give the code set a name.');
    if (!/^https?:\/\//i.test(pub.sourceUrl.trim())) return setPubErr('Source URL must start with http:// or https://');
    if (pub.license.trim().length < 5) return setPubErr('Say why this is free to host (e.g. "US government work").');
    const fd = new FormData();
    fd.append('file', pub.file);
    fd.append('name', pub.name.trim());
    fd.append('edition', pub.edition);
    fd.append('jurisdiction', pub.jurisdiction);
    fd.append('publisher', pub.publisher);
    fd.append('sourceUrl', pub.sourceUrl.trim());
    fd.append('license', pub.license.trim());
    setPubBusy(true);
    try {
      await api('/admin/sets', { method: 'POST', body: fd });
      setPub(EMPTY_PUB);
      if (pubFileInput.current) pubFileInput.current.value = '';
      await loadSets();
    } catch (ex) {
      setPubErr(ex.message);
    } finally {
      if (mounted.current) setPubBusy(false);
    }
  }

  async function onDelete(s) {
    const msg =
      s.scope === 'public'
        ? `Delete the PUBLIC set "${s.name}"? It disappears for every organization.`
        : `Delete "${s.name}"? This removes it and its search index.`;
    if (!window.confirm(msg)) return;
    try {
      await api(`/sets/${s.id}`, { method: 'DELETE' });
      await loadSets();
    } catch (ex) {
      window.alert(ex.message);
    }
  }

  const SpeechRec = typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

  function toggleMic() {
    setMicErr('');
    if (listening && recRef.current) {
      try { recRef.current.stop(); } catch (e) { /* ignore */ }
      return;
    }
    if (!SpeechRec) return;
    const rec = new SpeechRec();
    rec.lang = 'en-US';
    rec.interimResults = true;
    rec.continuous = false;
    const base = question.trim();
    rec.onresult = (ev) => {
      let text = '';
      for (let i = 0; i < ev.results.length; i++) text += ev.results[i][0].transcript + ' ';
      const spoken = normalizeSpoken(text);
      if (mounted.current) setQuestion(base ? `${base} ${spoken}` : spoken);
    };
    rec.onerror = (ev) => {
      if (!mounted.current) return;
      const code = ev && ev.error;
      setMicErr(
        code === 'not-allowed' || code === 'service-not-allowed'
          ? 'Microphone access is blocked. Allow it in your browser\'s site settings and try again.'
          : code === 'no-speech'
          ? "Didn't hear anything. Tap the mic and try again."
          : code === 'aborted'
          ? ''
          : 'Voice input failed. You can type your question instead.'
      );
    };
    rec.onend = () => {
      if (mounted.current) setListening(false);
      recRef.current = null;
    };
    try {
      recRef.current = rec;
      rec.start();
      setListening(true);
    } catch (e) {
      recRef.current = null;
      setListening(false);
      setMicErr('Voice input could not start. You can type your question instead.');
    }
  }

  async function onAsk() {
    setAskErr('');
    const q = question.trim();
    if (q.length < 5) return setAskErr('Ask a fuller question.');
    if (!selectedIds.length) return setAskErr('Select at least one ready code set.');
    setAsking(true);
    try {
      const j = await api('/ask', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ question: q, setIds: selectedIds }),
      });
      setResult(j);
      setFull({});
      if (j.usage) setUsage(j.usage);
    } catch (ex) {
      setAskErr(ex.message);
    } finally {
      if (mounted.current) setAsking(false);
    }
  }

  async function showFull(src) {
    try {
      const j = await api(`/sections/${src.sectionId}`);
      setFull((f) => ({ ...f, [src.sectionId]: j.section.text }));
    } catch (ex) {
      setFull((f) => ({ ...f, [src.sectionId]: `(${ex.message})` }));
    }
  }

  function scrollToSource(n) {
    const el = document.getElementById(`cp-src-${n}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  const jurisdictions = Array.from(new Set(sets.map((x) => (x.jurisdiction || '').trim()).filter(Boolean))).sort();
  const filtering = sets.length > 4;

  function visibleSets(scope) {
    const q = setQuery.trim().toLowerCase();
    const list = sets.filter((x) => {
      if ((x.scope === 'org') !== (scope === 'org')) return false;
      if (setJur && (x.jurisdiction || '').trim() !== setJur) return false;
      if (!q) return true;
      return [x.name, x.edition, x.jurisdiction, x.publisher].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
    const byName = (p, r) => String(p.name || '').localeCompare(String(r.name || ''));
    list.sort((p, r) => {
      if (setSort === 'jurisdiction') return String(p.jurisdiction || '~').localeCompare(String(r.jurisdiction || '~')) || byName(p, r);
      if (setSort === 'edition') return String(r.edition || '').localeCompare(String(p.edition || '')) || byName(p, r);
      return byName(p, r);
    });
    return list;
  }

  function setGroupSelected(list, on) {
    setUnchecked((u) => {
      const n = { ...u };
      list.forEach((x) => {
        if (x.status === 'ready') n[x.id] = !on;
      });
      return n;
    });
  }

  function renderSet(s) {
    const ready = s.status === 'ready';
    const bits = [s.edition, s.jurisdiction, s.publisher].filter(Boolean).join(' · ');
    return (
      <div key={s.id} style={S.set}>
        <input
          type="checkbox"
          aria-label={`Search ${s.name}`}
          style={{ marginTop: 4 }}
          disabled={!ready}
          checked={ready && !unchecked[s.id]}
          onChange={(e) => setUnchecked((u) => ({ ...u, [s.id]: !e.target.checked }))}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.setName}>
            {s.name}
            <span style={{ ...S.tag, ...(s.scope === 'org' ? S.tagPrivate : {}) }}>{s.scope === 'org' ? 'private' : 'public'}</span>
          </div>
          <div style={S.small}>
            {bits ? `${bits} — ` : ''}
            <span style={{ color: ready ? t.ok : s.status === 'failed' ? t.bad : t.warn }}>
              {ready ? `${s.sectionCount} sections` : s.status === 'failed' ? `Failed: ${s.error || 'unknown error'}` : 'Indexing…'}
            </span>
          </div>
          {s.scope === 'public' && s.license && (
            <div style={S.small}>
              Source:{' '}
              {/^https?:\/\//i.test(s.sourceUrl || '') ? (
                <a href={s.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: t.warn }}>
                  {s.sourceUrl.replace(/^https?:\/\/(www\.)?/i, '').slice(0, 48)}
                </a>
              ) : (
                s.sourceUrl
              )}{' '}
              ({s.license})
            </div>
          )}
        </div>
        {(s.scope === 'org' || isAdmin) && (
          <button type="button" style={S.xBtn} title="Delete" aria-label={`Delete ${s.name}`} onClick={() => onDelete(s)}>
            ×
          </button>
        )}
      </div>
    );
  }

  function renderGroup(title, scope) {
    const list = visibleSets(scope);
    const total = sets.filter((x) => (x.scope === 'org') === (scope === 'org')).length;
    if (!total) return null;
    return (
      <div>
        <div style={S.groupHead}>
          <span>
            {title} ({list.length}
            {list.length !== total ? ` of ${total}` : ''})
          </span>
          <span style={S.groupLinks}>
            <button type="button" style={S.linkBtn} onClick={() => setGroupSelected(list, true)}>All</button>
            {' · '}
            <button type="button" style={S.linkBtn} onClick={() => setGroupSelected(list, false)}>None</button>
          </span>
        </div>
        {list.length ? list.map(renderSet) : <div style={S.small}>No matches.</div>}
      </div>
    );
  }

  function sourceRef(s) {
    return [s.setName, s.edition ? `(${s.edition})` : '', s.sectionNumber ? `Section ${s.sectionNumber}` : '', s.title || '', s.page ? `p. ${s.page}` : '']
      .filter(Boolean)
      .join(' ');
  }

  // Plain-text version of the result: answer, then the numbered sources it cites.
  function resultAsText() {
    if (!result) return '';
    const lines = [String(result.answer || '').trim()];
    if (result.sources && result.sources.length) {
      lines.push('', 'Sources:');
      result.sources.forEach((s) => lines.push(`[${s.n}] ${sourceRef(s)}`));
    }
    lines.push('', 'Research aid only. Verify against the adopted edition and local amendments; the engineer of record and the authority having jurisdiction decide compliance.');
    return lines.join('\n');
  }

  async function copyResult() {
    const text = resultAsText();
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (e) {
      /* fall through to the legacy path */
    }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (e) {
        ok = false;
      }
    }
    if (ok && mounted.current) {
      setCopied(true);
      setTimeout(() => mounted.current && setCopied(false), 1800);
    }
  }

  function renderAnswer(text) {
    // [n] -> clickable button, built as React nodes (never innerHTML)
    return String(text)
      .split(/(\[\d{1,2}\])/)
      .map((part, i) => {
        const m = part.match(/^\[(\d{1,2})\]$/);
        if (!m) return <React.Fragment key={i}>{part}</React.Fragment>;
        return (
          <button key={i} type="button" style={S.cite} onClick={() => scrollToSource(m[1])}>
            {m[1]}
          </button>
        );
      });
  }

  if (!loaded) return <div style={S.blocked}>Loading Code+…</div>;
  if (blocked) return <div style={S.blocked}>{blocked}</div>;

  return (
    <div style={S.page}>
      <h1 style={S.h1}>
        Code<span style={{ color: t.gold }}>+</span>
      </h1>
      <p style={S.sub}>AI code research with citations. A research aid, not a compliance determination.</p>

      <div style={S.grid}>
        <aside>
          <div style={S.card}>
            <h2 style={S.h2}>Code sets</h2>
            {!sets.length && <div style={S.small}>No code sets yet. Add your licensed code set below.</div>}
            {filtering && (
              <>
                <input
                  style={{ ...S.input, marginBottom: 6 }}
                  placeholder="Search code sets…"
                  aria-label="Search code sets"
                  value={setQuery}
                  onChange={(e) => setSetQuery(e.target.value)}
                />
                <div style={S.filterRow}>
                  <select style={S.select} aria-label="Filter by jurisdiction" value={setJur} onChange={(e) => setSetJur(e.target.value)}>
                    <option value="">All jurisdictions</option>
                    {jurisdictions.map((j) => (
                      <option key={j} value={j}>{j}</option>
                    ))}
                  </select>
                  <select style={S.select} aria-label="Sort code sets" value={setSort} onChange={(e) => setSetSort(e.target.value)}>
                    <option value="name">Sort: Name</option>
                    <option value="jurisdiction">Sort: Jurisdiction</option>
                    <option value="edition">Sort: Newest edition</option>
                  </select>
                </div>
              </>
            )}
            <div style={filtering ? S.setList : undefined}>
              {renderGroup('Your organization', 'org')}
              {renderGroup('Public codes', 'public')}
            </div>
            {sets.length > 0 && (
              <div style={{ ...S.small, marginTop: 8 }}>
                {selectedIds.length} of {sets.filter((x) => x.status === 'ready').length} sets selected for the next question
              </div>
            )}
          </div>

          <div style={S.card}>
            <h2 style={S.h2}>Add your licensed code set</h2>
            <form onSubmit={onUpload}>
              <label style={S.label} htmlFor="cp-file">File (PDF, DOCX or TXT)</label>
              <div style={S.fileRow}>
                <input
                  id="cp-file"
                  ref={fileInput}
                  type="file"
                  accept=".pdf,.docx,.txt,.md"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = (e.target.files && e.target.files[0]) || null;
                    setForm((f) => {
                      if (!file) return { ...f, file };
                      const g = guessFromFilename(file.name);
                      return { ...f, file, name: f.name || g.name, edition: f.edition || g.edition, jurisdiction: f.jurisdiction || g.jurisdiction, publisher: f.publisher || g.publisher };
                    });
                  }}
                />
                <button type="button" style={S.btn} onClick={() => fileInput.current && fileInput.current.click()}>
                  Choose File
                </button>
                <span style={S.fileName}>{form.file ? form.file.name : 'No file chosen'}</span>
              </div>
              <label style={S.label} htmlFor="cp-name">Name</label>
              <input id="cp-name" style={S.input} placeholder="e.g. 2022 California Mechanical Code" value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              <div style={S.row2}>
                <div>
                  <label style={S.label} htmlFor="cp-ed">Edition</label>
                  <input id="cp-ed" style={S.input} placeholder="2022" value={form.edition}
                    onChange={(e) => setForm((f) => ({ ...f, edition: e.target.value }))} />
                </div>
                <div>
                  <label style={S.label} htmlFor="cp-jur">Jurisdiction</label>
                  <input id="cp-jur" style={S.input} placeholder="CA" value={form.jurisdiction}
                    onChange={(e) => setForm((f) => ({ ...f, jurisdiction: e.target.value }))} />
                </div>
              </div>
              <label style={S.label} htmlFor="cp-pub">Publisher</label>
              <input id="cp-pub" style={S.input} placeholder="ICC, IAPMO, NFPA…" value={form.publisher}
                onChange={(e) => setForm((f) => ({ ...f, publisher: e.target.value }))} />
              <div style={S.attest}>
                <input id="cp-att" type="checkbox" style={{ marginTop: 3 }} checked={form.attest}
                  onChange={(e) => setForm((f) => ({ ...f, attest: e.target.checked }))} />
                <label htmlFor="cp-att">{attestText}</label>
              </div>
              <button type="submit" style={{ ...S.btn, ...(uploading ? S.btnOff : {}) }} disabled={uploading}>
                {uploading ? 'Uploading…' : 'Upload & index'}
              </button>
              {uploadErr && <div style={S.err} role="alert">{uploadErr}</div>}
            </form>
            <div style={S.notice}>
              Uploaded sets are private to your organization. They are never shown to or searched by other organizations, and you can delete them at any time.
            </div>
          </div>
          {isAdmin && (
            <div style={S.card}>
              <h2 style={S.h2}>Admin: add a public set</h2>
              <div style={S.small}>
                Visible to every organization, with its source and license. Only add content you have verified is free to host
                (US government works, state-authored regulations, local government codes). Not for ICC, IAPMO, NFPA, ASHRAE or ASTM text.
              </div>
              <form onSubmit={onPublish}>
                <label style={S.label} htmlFor="cp-pfile">File (PDF, DOCX or TXT)</label>
                <div style={S.fileRow}>
                  <input
                    id="cp-pfile"
                    ref={pubFileInput}
                    type="file"
                    accept=".pdf,.docx,.txt,.md"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = (e.target.files && e.target.files[0]) || null;
                      setPub((f) => {
                        if (!file) return { ...f, file };
                        const g = guessFromFilename(file.name);
                        return { ...f, file, name: f.name || g.name, edition: f.edition || g.edition, jurisdiction: f.jurisdiction || g.jurisdiction, publisher: f.publisher || g.publisher };
                      });
                    }}
                  />
                  <button type="button" style={S.btn} onClick={() => pubFileInput.current && pubFileInput.current.click()}>
                    Choose File
                  </button>
                  <span style={S.fileName}>{pub.file ? pub.file.name : 'No file chosen'}</span>
                </div>
                <label style={S.label} htmlFor="cp-pname">Name</label>
                <input id="cp-pname" style={S.input} placeholder="e.g. OSHA General Industry (29 CFR 1910)" value={pub.name}
                  onChange={(e) => setPub((f) => ({ ...f, name: e.target.value }))} />
                <div style={S.row2}>
                  <div>
                    <label style={S.label} htmlFor="cp-ped">Edition</label>
                    <input id="cp-ped" style={S.input} placeholder="2026" value={pub.edition}
                      onChange={(e) => setPub((f) => ({ ...f, edition: e.target.value }))} />
                  </div>
                  <div>
                    <label style={S.label} htmlFor="cp-pjur">Jurisdiction</label>
                    <input id="cp-pjur" style={S.input} placeholder="US, CA, CA - Cupertino" value={pub.jurisdiction}
                      onChange={(e) => setPub((f) => ({ ...f, jurisdiction: e.target.value }))} />
                  </div>
                </div>
                <label style={S.label} htmlFor="cp-ppub">Publisher</label>
                <input id="cp-ppub" style={S.input} placeholder="US DOL / OSHA, CEC, City of Cupertino…" value={pub.publisher}
                  onChange={(e) => setPub((f) => ({ ...f, publisher: e.target.value }))} />
                <label style={S.label} htmlFor="cp-psrc">Source URL (required)</label>
                <input id="cp-psrc" style={S.input} placeholder="https://www.ecfr.gov/current/title-29/part-1910" value={pub.sourceUrl}
                  onChange={(e) => setPub((f) => ({ ...f, sourceUrl: e.target.value }))} />
                <label style={S.label} htmlFor="cp-plic">Why it is free to host (required)</label>
                <input id="cp-plic" style={S.input} placeholder="US government work; not subject to copyright" value={pub.license}
                  onChange={(e) => setPub((f) => ({ ...f, license: e.target.value }))} />
                <div style={{ marginTop: 12 }}>
                  <button type="submit" style={{ ...S.btn, ...(pubBusy ? S.btnOff : {}) }} disabled={pubBusy}>
                    {pubBusy ? 'Uploading…' : 'Publish public set'}
                  </button>
                </div>
                {pubErr && <div style={S.err} role="alert">{pubErr}</div>}
              </form>
            </div>
          )}
        </aside>

        <main>
          <div style={S.card}>
            <h2 style={S.h2}>Ask a code question</h2>
            <textarea
              style={{ ...S.input, minHeight: 84, resize: 'vertical' }}
              placeholder="e.g. What are the outdoor air requirements for an office space? Or: What does 506.3 require for a Type I hood duct?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') onAsk(); }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center' }}>
              <button type="button" style={{ ...S.btn, ...(asking ? S.btnOff : {}) }} disabled={asking} onClick={onAsk}>
                {asking ? 'Searching…' : 'Ask'}
              </button>
              {SpeechRec && (
                <button
                  type="button"
                  style={{ ...S.micBtn, ...(listening ? S.micOn : {}) }}
                  onClick={toggleMic}
                  aria-pressed={listening}
                  aria-label={listening ? 'Stop voice input' : 'Ask by voice'}
                  title="Dictate your question (your browser's speech service transcribes the audio)"
                >
                  {listening ? '● Listening… tap to stop' : '🎤 Speak'}
                </button>
              )}
              {usage && usage.limit > 0 && (
                <span style={{ marginLeft: 'auto', ...S.small }}>
                  {usage.used} / {usage.limit} questions today
                </span>
              )}
            </div>
            {askErr && <div style={S.err} role="alert">{askErr}</div>}
            {micErr && <div style={S.err} role="alert">{micErr}</div>}
          </div>

          {result && (
            <>
              <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h2 style={{ ...S.h2, marginBottom: 10 }}>Answer</h2>
                  <button type="button" style={S.linkBtn} onClick={copyResult} aria-label="Copy answer and sources">
                    {copied ? 'Copied ✓' : 'Copy'}
                  </button>
                </div>
                <p style={S.answer}>{renderAnswer(result.answer)}</p>
                <div style={S.notice}>
                  Verify against the adopted edition and local amendments. The engineer of record and the authority having jurisdiction decide compliance.
                </div>
              </div>
              {result.sources && result.sources.length > 0 && (
                <div style={S.card}>
                  <h2 style={S.h2}>Sources</h2>
                  {result.sources.map((s) => {
                    const ref = sourceRef(s);
                    const fullText = full[s.sectionId];
                    return (
                      <div key={s.n} id={`cp-src-${s.n}`} style={S.source}>
                        <div style={{ fontWeight: 600 }}>[{s.n}] {ref}</div>
                        <div style={S.snip}>{fullText || s.snippet + (s.snippet && s.snippet.length >= 280 ? '…' : '')}</div>
                        {!fullText && (
                          <button type="button" style={S.linkBtn} onClick={() => showFull(s)}>Show full section</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}